/**
 * Lead Drop Form 5018 — the KUK hero card (inline lead form).
 *
 * This is NOT an inline skin of popup 5018. It is the collector KUK pins to the
 * right of the hero: labelled Name / Phone / Email / Select Course shown all at
 * once, and Apply Now hands those four values to popup 5018, which runs the lead
 * lookup and the OTP.
 *
 * So the settings here are deliberately a small set. Everything that belongs to
 * the OTP screen, the verified card and the success cards lives on the POPUP row
 * (Lead Drop Popup 5018), because those screens are the popup's. Duplicating them
 * here would let an editor style screens this surface never renders.
 *
 * Note there is no qualification field: the reference hero card does not ask for
 * it, and the popup collects it after the handoff.
 */

/**
 * Real `lead_drop_form` columns. Anything NOT listed here must be flagged
 * `isJsonField` so Form_4 nests it under `additional_fields` — Sequelize silently
 * drops unknown top-level keys, which would lose the value on save.
 *
 * `form_title` is deliberately absent: the form table calls that column `title`,
 * so it rides in `additional_fields` like the rest.
 */
const LEAD_DROP_FORM_COLUMNS = new Set([
  "checkbox_description",
  "checkbox_color",
  "terms_conditions_link",
  "privacy_policy_link",
  "button_text",
  "button_text_color",
  "button_background_color",
]);

type FieldConfig = {
  fieldName: string;
  label: string;
  value: string | boolean;
  valueType: "string" | "boolean";
  filedType: "INPUT" | "TEXT_AREA" | "COLOR" | "FILE" | "CHECKBOX" | "NUMBER";
  placeholder: string;
  required: boolean;
  defaultValue: string | boolean;
  isJsonField?: boolean;
  rows?: number;
  description?: string;
};

const mk = (
  fieldName: string,
  label: string,
  defaultValue: string | boolean,
  filedType: FieldConfig["filedType"],
  extra: Partial<FieldConfig> = {},
): FieldConfig => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: typeof defaultValue === "boolean" ? "boolean" : "string",
  filedType,
  placeholder:
    extra.placeholder ??
    (filedType === "COLOR"
      ? `Enter ${label} (e.g., ${defaultValue || "#FFFFFF"})`
      : `Enter ${label}`),
  required: false,
  defaultValue,
  ...(LEAD_DROP_FORM_COLUMNS.has(fieldName) ? {} : { isJsonField: true }),
  ...extra,
});

const text = (n: string, l: string, d: string, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "INPUT", e);
const area = (n: string, l: string, d: string, rows = 2, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "TEXT_AREA", { rows, ...e });
const color = (n: string, l: string, d: string, e?: Partial<FieldConfig>) =>
  mk(n, l, d, "COLOR", e);

const LEAD_DROP_FORM_5018_FIELDS: FieldConfig[] = [
  // ── Heading ─────────────────────────────────────────────────────────────────
  area(
    "form_title",
    "Card Heading",
    "Share your details and we will get in touch with you.",
    2,
  ),
  color("form_title_color", "Card Heading Color", "#031F44"),

  // ── Field labels & placeholders ─────────────────────────────────────────────
  // The hero card shows a label above every field, each with a red asterisk.
  text("name_label", "Name — Label", "Enter Name"),
  text("name_placeholder", "Name — Placeholder", "Enter Name"),
  text("phone_label", "Phone — Label", "Phone Number"),
  text("phone_placeholder", "Phone — Placeholder", "Enter your phone number"),
  text("email_label", "Email — Label", "Email ID"),
  text("email_placeholder", "Email — Placeholder", "Enter your Email ID"),
  text("course_label", "Course — Label", "Select Course"),
  text("course_placeholder", "Course — Placeholder", "Select Course"),
  color("field_label_color", "Field Label Color", "#031F44"),
  color("required_marker_color", "Required Asterisk Color", "#9F1C33"),
  color("error_text_color", "Validation Error Text Color", "#FF0000"),

  // ── Submit button ───────────────────────────────────────────────────────────
  text("button_text", "Submit Button Text", "Apply Now", {
    description:
      "Opens the lead popup with the details entered here, which then verifies the number.",
  }),
  color("button_text_color", "Submit Button Text Color", "#FFFFFF"),
  color("button_background_color", "Submit Button Background Color", "#9F1C33"),

  // ── Consent ─────────────────────────────────────────────────────────────────
  // Plain copy under the button on this card; the popup carries the checkbox.
  area(
    "consent_text",
    "Consent Text",
    "By Clicking on sign up I allow Kurukshetra University and its associates to contact me and send program communication via email / SMS / WhatsApp/ voice call etc.",
    3,
  ),
  text("terms_conditions_link", "Terms & Conditions Link", "", {
    placeholder: "https://example.com/terms-and-conditions",
    description:
      "Destination URL for link slot 1. Referenced in Consent Text as {{1:Your link text}}.",
  }),
  text("privacy_policy_link", "Privacy Policy Link", "", {
    placeholder: "https://example.com/privacy-policy",
    description:
      "Destination URL for link slot 2. Referenced in Consent Text as {{2:Your link text}}.",
  }),
  color("consent_text_color", "Consent Text Color", "#101112"),
  color("accent_color", "Consent Link Color", "#9F1C33"),
];

/** `DEFAULT_ROW` is derived from the field list so the two can never drift apart. */
const DEFAULT_ROW = LEAD_DROP_FORM_5018_FIELDS.reduce<Record<string, unknown>>(
  (row, field) => {
    row[field.fieldName] = field.defaultValue;
    return row;
  },
  {},
);

export const LEAD_DROP_FORM_5018_CONSTANTS = {
  DEFAULT_ROW,
  LEAD_DROP_FORM_FIELDS_CONFIG: LEAD_DROP_FORM_5018_FIELDS,
  METADATA_FIELDS: [],
};
